import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dropdown,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Spinner,
  Text,
  Textarea
} from '@fluentui/react-components';
import { useTranslation, Trans } from 'react-i18next';
import { CurrencySelectorComponent } from '../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import * as ComponentFunctions from './ConceptValuesForm.Functions';
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

export const ConceptValuesFormComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();

  const [values, setValues] = useState<ComponentFunctions.FormValues>(
    ComponentFunctions.initFormValues(props.concept)
  );
  const [submitted, setSubmitted] = useState(false);
  const [validation, setValidation] = useState<ComponentFunctions.FormValidation | undefined>();
  const [pendingSponsorId, setPendingSponsorId] = useState<string | undefined>();
  const [pendingConceptName, setPendingConceptName] = useState<string | undefined>();

  // Reinicializar al abrir / cambiar concepto
  useEffect(() => {
    if (props.isOpen) {
      setValues(ComponentFunctions.initFormValues(props.concept));
      setSubmitted(false);
      setValidation(undefined);
      if (!props.isLoadingCategories && props.categories.length === 0) {
        props.GetAllConceptCategories();
      }
    }
  }, [props.isOpen, props.concept?.id]);

  // Cerrar tras actualizar exitosamente
  useEffect(() => {
    if (submitted && props.successUpdating) {
      props.onSuccess?.();
      props.dismissPanel();
    }
  }, [props.successUpdating]);

  // Cerrar (y asociar al sponsor) tras crear exitosamente
  useEffect(() => {
    if (!submitted || !props.successAdding) return;
    if (pendingSponsorId && pendingConceptName) {
      const newConcept = props.concepts.find((c) => c.conceptName === pendingConceptName);
      if (newConcept) {
        props.AddConceptSponsor({ conceptId: newConcept.id, sponsorId: pendingSponsorId });
      }
      setPendingSponsorId(undefined);
      setPendingConceptName(undefined);
    }
    props.onSuccess?.();
    props.dismissPanel();
  }, [props.successAdding]);

  // El valor se toma del `data` de Fluent v9 en el momento del evento (no dentro del updater).
  const set = (field: keyof ComponentFunctions.FormValues) =>
    (_ev: unknown, data: { value: string }) =>
      setValues((prev) => ({ ...prev, [field]: data.value }));

  const handleSave = () => {
    setSubmitted(true);
    const v = ComponentFunctions.validateForm(values, t);
    setValidation(v);
    if (!v.success) return;
    if (props.concept) {
      props.UpdateConcept(ComponentFunctions.buildUpdatedConcept(props.concept, values));
    } else {
      setPendingConceptName(values.conceptName.trim());
      if (props.sponsorId) {
        setPendingSponsorId(props.sponsorId);
      }
      props.AddConcept(ComponentFunctions.buildNewConcept(values));
    }
  };

  const handleDismiss = () => {
    setSubmitted(false);
    setValidation(undefined);
    props.dismissPanel();
  };

  const isSaving = !!(props.isUpdating || props.isAdding);
  const currencySuffix = values.costCurrency ? <Text size={200}>{values.costCurrency}</Text> : undefined;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      eyebrow={props.concept?.conceptName || undefined}
      title={
        props.concept
          ? t('conceptValuesForm.title', 'Editar Concepto')
          : t('conceptValuesForm.titleNew', 'Nuevo Concepto')
      }
      onDismiss={handleDismiss}
      testId="concept-values-form-drawer"
      secondaryAction={{
        label: <Trans i18nKey="conceptValuesForm.cancelButton" defaults="Cancelar" />,
        onClick: handleDismiss
      }}
      primaryAction={{
        label: <Trans i18nKey="conceptValuesForm.saveButton" defaults="Guardar" />,
        onClick: handleSave,
        loading: isSaving
      }}
    >
      {submitted && validation && !validation.success && (
        <GuideMessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>
              {t('conceptValuesForm.reviewInformation', 'Por favor revise los siguientes inconvenientes:')}
            </MessageBarTitle>
            <ul>
              {validation.errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {props.errorMessage && (
        <GuideMessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerForm onSubmit={handleSave} testId="concept-values-form">
        {/* Información general */}
        <GuideDrawerSection title={t('conceptValuesForm.section.general', 'Información general')}>
          <GuideField
            label={t('conceptValuesForm.fields.name', 'Nombre')}
            required
            error={
              submitted && !values.conceptName.trim()
                ? t('conceptValuesForm.validation.nameRequired', 'El nombre es obligatorio')
                : undefined
            }
          >
            <Input
              value={values.conceptName}
              onChange={set('conceptName')}
              placeholder={t('conceptValuesForm.fields.namePlaceholder', 'Ingrese el nombre del concepto')}
            />
          </GuideField>

          <GuideField label={t('conceptValuesForm.fields.description', 'Descripción')}>
            <Textarea
              value={values.conceptDescription}
              onChange={set('conceptDescription')}
              placeholder={t('conceptValuesForm.fields.descriptionPlaceholder', 'Ingrese la descripción')}
              rows={3}
              resize="vertical"
            />
          </GuideField>

          <GuideFieldGrid>
            <GuideField label={t('conceptValuesForm.fields.code', 'Código')}>
              <Input
                value={values.conceptCode}
                onChange={set('conceptCode')}
                placeholder={t('conceptValuesForm.fields.codePlaceholder', 'Ingrese el código del concepto')}
              />
            </GuideField>

            <GuideField label={t('conceptValuesForm.fields.category', 'Categoría')}>
              {props.isLoadingCategories ? (
                <Spinner size="tiny" />
              ) : (
                <Dropdown
                  placeholder={t('conceptValuesForm.fields.categoryPlaceholder', 'Seleccione una categoría')}
                  value={
                    props.categories.find((c) => c.id === values.conceptCategoryId)
                      ?.conceptCategoryName ?? ''
                  }
                  selectedOptions={
                    values.conceptCategoryId != null ? [String(values.conceptCategoryId)] : []
                  }
                  onOptionSelect={(_: SelectionEvents, data: OptionOnSelectData) => {
                    const id = data.optionValue ? parseInt(data.optionValue, 10) : undefined;
                    setValues((prev) => ({ ...prev, conceptCategoryId: id }));
                  }}
                >
                  <Option value="" text="">
                    {t('conceptValuesForm.fields.categoryNone', '— Sin categoría —')}
                  </Option>
                  {props.categories.map((cat) => (
                    <Option key={cat.id} value={String(cat.id)} text={cat.conceptCategoryName ?? ''}>
                      {cat.conceptCategoryName}
                    </Option>
                  ))}
                </Dropdown>
              )}
            </GuideField>
          </GuideFieldGrid>
        </GuideDrawerSection>

        {/* Precios */}
        <GuideDrawerSection title={t('conceptValuesForm.section.pricing', 'Precios')}>
          <CurrencySelectorComponent
            label={t('conceptValuesForm.fields.currency', 'Moneda')}
            defaultSelected={values.costCurrency}
            errorMessage={undefined}
            onCurrencyChange={(currency) =>
              setValues((prev) => ({ ...prev, costCurrency: currency }))
            }
          />

          <GuideFieldGrid>
            <GuideField label={t('conceptValuesForm.fields.referenceCost', 'Costo de referencia')}>
              <Input
                type="number"
                value={values.referenceCost}
                onChange={set('referenceCost')}
                placeholder="0.00"
                contentAfter={currencySuffix}
              />
            </GuideField>

            <GuideField label={t('conceptValuesForm.fields.sponsorCost', 'Costo Sponsor')}>
              <Input
                type="number"
                value={values.sponsorWeigthedCost}
                onChange={set('sponsorWeigthedCost')}
                placeholder="0.00"
                contentAfter={currencySuffix}
              />
            </GuideField>

            <GuideField label={t('conceptValuesForm.fields.siteCost', 'Costo Sitio')}>
              <Input
                type="number"
                value={values.siteWeigthedCost}
                onChange={set('siteWeigthedCost')}
                placeholder="0.00"
                contentAfter={currencySuffix}
              />
            </GuideField>

            <GuideField label={t('conceptValuesForm.fields.countryCost', 'Costo País')}>
              <Input
                type="number"
                value={values.countryWeigthedCost}
                onChange={set('countryWeigthedCost')}
                placeholder="0.00"
                contentAfter={currencySuffix}
              />
            </GuideField>

            <GuideField label={t('conceptValuesForm.fields.globalCost', 'Costo Global')}>
              <Input
                type="number"
                value={values.globalWeigthedCost}
                onChange={set('globalWeigthedCost')}
                placeholder="0.00"
                contentAfter={currencySuffix}
              />
            </GuideField>
          </GuideFieldGrid>
        </GuideDrawerSection>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

const ConnectedComponent = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ConceptValuesFormComponent as any);

export default ConnectedComponent as React.ComponentType<ComponentFunctions.ConceptValuesFormOwnProps>;
